import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { PHONE_COUNTRIES } from '../config/phoneCountries';
import '../styles/PhoneInput.css';

/**
 * Reusable phone input with country code prefix selector.
 *
 * Parent form only sees a single concatenated string (e.g. "+966542111111").
 * All phone logic (country selection, paste detection, cleaning) is encapsulated here.
 *
 * i18n: This component uses hardcoded Arabic, matching its host components
 * (BookingModal, EditBookingModal) which are not yet translated (Phase 2).
 * Country config has nameAr/nameEn fields ready for when Phase 2 lands.
 *
 * @param {string}   value          - Full phone string (e.g. "+966542111111") or empty
 * @param {string}   defaultCountry - ISO country code for default selection (default: "SA")
 * @param {function} onChange       - Called with the full concatenated phone string
 * @param {boolean}  disabled       - Disable input
 * @param {boolean}  required       - Mark as required
 */
const PhoneInput = ({ value = '', defaultCountry = 'SA', onChange, disabled = false, required = false }) => {
  // Resolve initial country from value or default
  const resolveInitial = () => {
    if (value) {
      const match = findCountryByNumber(value);
      if (match) {
        return {
          dial: match.dial,
          local: cleanLocal(value.slice(match.dial.length)),
        };
      }
    }
    const country = PHONE_COUNTRIES.find(c => c.code === defaultCountry) || PHONE_COUNTRIES[0];
    return { dial: country.dial, local: '' };
  };

  const initial = resolveInitial();
  const [dialCode, setDialCode] = useState(initial.dial);
  const [localNumber, setLocalNumber] = useState(initial.local);
  const inputRef = useRef(null);

  // Sync internal state when value prop changes externally (e.g. EditBookingModal loading data)
  useEffect(() => {
    const currentFull = localNumber ? dialCode + localNumber : '';
    if (value !== currentFull) {
      if (value) {
        const match = findCountryByNumber(value);
        if (match) {
          setDialCode(match.dial);
          setLocalNumber(cleanLocal(value.slice(match.dial.length)));
        } else {
          // Unknown prefix — put digits in local field
          setLocalNumber(cleanLocal(value.replace(/^\+|^00/, '')));
        }
      } else {
        const country = PHONE_COUNTRIES.find(c => c.code === defaultCountry) || PHONE_COUNTRIES[0];
        setDialCode(country.dial);
        setLocalNumber('');
      }
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Helpers ---

  /** Strip non-digit characters, then strip leading zero */
  function cleanLocal(raw) {
    const digitsOnly = raw.replace(/[^\d]/g, '');
    return digitsOnly.replace(/^0+/, '');
  }

  /** Find a country whose dial code matches the start of a full number */
  function findCountryByNumber(phone) {
    const normalized = phone.startsWith('00') ? '+' + phone.slice(2) : phone;
    if (!normalized.startsWith('+')) return null;
    // Sort by dial length descending so +971 matches before +9
    const sorted = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
    return sorted.find(c => normalized.startsWith(c.dial)) || null;
  }

  /** Get the current country object */
  function currentCountry() {
    return PHONE_COUNTRIES.find(c => c.dial === dialCode) || PHONE_COUNTRIES[0];
  }

  /** Emit the concatenated value to parent */
  const emit = useCallback((dial, local) => {
    if (!local) {
      onChange('');
      return;
    }
    onChange(dial + local);
  }, [onChange]);

  // --- Handlers ---

  const handleDialChange = (e) => {
    const newDial = e.target.value;
    setDialCode(newDial);
    emit(newDial, localNumber);
  };

  const handleLocalChange = (e) => {
    const raw = e.target.value;
    // Allow only digits and spaces while typing
    const filtered = raw.replace(/[^\d\s]/g, '');
    const cleaned = cleanLocal(filtered);
    setLocalNumber(cleaned);
    emit(dialCode, cleaned);
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text').trim();

    // Detect full international number (starts with + or 00)
    if (pasted.startsWith('+') || pasted.startsWith('00')) {
      e.preventDefault();
      const match = findCountryByNumber(pasted);
      if (match) {
        const normalized = pasted.startsWith('00') ? '+' + pasted.slice(2) : pasted;
        const local = cleanLocal(normalized.slice(match.dial.length));
        setDialCode(match.dial);
        setLocalNumber(local);
        emit(match.dial, local);
      } else {
        // Unknown country — put everything in local, let backend normalize
        const local = cleanLocal(pasted.replace(/^\+|^00/, ''));
        setLocalNumber(local);
        emit(dialCode, local);
      }
      return;
    }

    // Regular local number paste — let handleLocalChange deal with it via default behavior
  };

  // --- Validation hint ---
  const country = currentCountry();
  const showHint = localNumber.length > 0 && !country.digits.includes(localNumber.length);

  return (
    <div>
      <div className="phone-input-group">
        <Form.Select
          className="phone-country-select touch-select"
          value={dialCode}
          onChange={handleDialChange}
          disabled={disabled}
          aria-label="رمز الدولة"
        >
          {PHONE_COUNTRIES.map(c => (
            <option key={c.code} value={c.dial}>
              {c.dial} {c.nameAr}
            </option>
          ))}
        </Form.Select>
        <Form.Control
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          className="phone-local-input touch-input"
          value={localNumber}
          onChange={handleLocalChange}
          onPaste={handlePaste}
          placeholder={country.placeholder}
          disabled={disabled}
          required={required}
          aria-label="رقم الهاتف المحلي"
        />
      </div>
      {showHint && (
        <Form.Text className="text-danger">
          يجب أن يكون الرقم {country.digits.join(' أو ')} أرقام
        </Form.Text>
      )}
    </div>
  );
};

export default PhoneInput;
